/**
 * Browser → BFF Call-History enrichment client (CCF-104).
 *
 * When the agent OPENS a call on the Call History screen (SCRN-13), the screen
 * lazily fetches the Genesys-native fields — Queue, Wrap Code and the AI Call
 * Summary — for that one call. The browser cannot reach the Genesys API
 * directly (CORS / no long-lived credentials), so the BFF performs the Genesys
 * reads server-side (Analytics Conversation Detail + routing lookups +
 * conversation summary) using the Genesys token held in the SERVER-SIDE session
 * (#343 — the browser holds no token and forwards no header), keyed on the
 * call's correlation id (conversationId).
 *
 * Best-effort: any field the BFF could not source is simply absent, and a
 * transport/non-2xx failure resolves to an empty object so the screen degrades
 * to its "—" placeholders rather than throwing.
 */

import { api } from '../api/client.js';

/** The Genesys-native enrichment for one Online call (fields optional). */
export interface CallEnrichment {
  queue?: string;
  wrapCode?: string;
  callSummary?: string;
}

/**
 * Fetches the Genesys-native enrichment for one opened call.
 *
 * @param customerNumber The CIF whose Call History is being reviewed. The BFF
 *   only enriches a `correlationId` that belongs to one of this customer's
 *   call-history records (it rejects an unknown id 404), so the enrichment
 *   cannot be used to read Genesys data for an arbitrary conversation.
 * @param correlationId The Genesys conversationId of the OPENED (possibly
 *   past) call being reviewed — NOT necessarily the agent's CURRENT live
 *   interaction (an agent can review history from an earlier call while on a
 *   different one now, or while Offline).
 * @param tellerId      The agent's 4-digit Teller ID (mandatory on the BFF call).
 * @param callType      "Online" / "Offline" — the BFF short-circuits Offline.
 * @param interactionId The agent's CURRENT live Genesys interaction id
 *   (`customer.contextConversationId`), distinct from `correlationId` above.
 *   The BFF's `/calls/enrichment` route is RBAC-gated through the same
 *   Online/Offline resolver as every other #270-hardened route, which needs
 *   this header to resolve the agent's OWN current state server-side (ai-review
 *   MEDIUM, #270) — forwarding it here does not change which conversation is
 *   enriched (that is still `correlationId`, re-validated against the
 *   customer's own call history server-side). The Genesys ACCESS TOKEN is NOT
 *   forwarded (#343 — the BFF reads it from the server-side session).
 */
export async function fetchCallEnrichment(
  customerNumber: string,
  correlationId: string,
  tellerId: string,
  callType?: 'Online' | 'Offline',
  interactionId?: string,
): Promise<CallEnrichment> {
  try {
    const { data, error } = await api.POST('/calls/enrichment', {
      params: {
        header: {
          'X-Teller-Id': tellerId,
          ...(interactionId
            ? { 'X-Genesys-Interaction-Id': interactionId }
            : {}),
        },
      },
      body: {
        customerNumber,
        correlationId,
        ...(callType ? { callType } : {}),
      },
    });
    if (error || !data) return {};
    return data as CallEnrichment;
  } catch {
    return {};
  }
}
